// arise-frontend/src/components/auth/AuthCard.jsx
// Shared shell + form primitives for the login / register pages. Uses only the
// existing design tokens and the existing .glass class (no new CSS).
import { useState } from 'react';

const labelStyle = {
  display: 'block', marginBottom: 6,
  fontFamily: 'Space Mono, monospace', fontSize: 10, letterSpacing: 1.5,
  textTransform: 'uppercase', color: '#a0c4d8',
};

function inputStyle(hasError, padRight) {
  return {
    width: '100%',
    padding: `11px ${padRight ? 64 : 12}px 11px 12px`,
    background: 'rgba(2,8,16,0.9)',
    border: `1px solid ${hasError ? 'rgba(255,60,110,0.6)' : 'rgba(0,240,255,0.18)'}`,
    borderRadius: 4,
    color: '#e0f4ff',
    fontFamily: 'Space Mono, monospace', fontSize: 12,
    transition: 'border-color 0.2s',
  };
}

export function AuthField({ id, label, error, hint, optional, reveal, type = 'text', ...inputProps }) {
  const [shown, setShown] = useState(false);
  const showHint = Boolean(hint) && !error;
  const describedBy = [error && `${id}-error`, showHint && `${id}-hint`].filter(Boolean).join(' ') || undefined;

  return (
    <div style={{ marginBottom: '1rem', textAlign: 'left' }}>
      <label htmlFor={id} style={labelStyle}>
        {label}
        {optional && <span style={{ color: '#6b8fa8', textTransform: 'none', letterSpacing: 0 }}> (optional)</span>}
      </label>
      <div style={{ position: 'relative' }}>
        <input
          id={id}
          type={reveal && shown ? 'text' : type}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          style={inputStyle(Boolean(error), Boolean(reveal))}
          {...inputProps}
        />
        {reveal && (
          <button
            type="button"
            onClick={() => setShown(s => !s)}
            aria-label={shown ? 'Hide password' : 'Show password'}
            aria-pressed={shown}
            style={{
              position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
              background: 'none', border: '1px solid transparent', borderRadius: 3,
              padding: '4px 8px', cursor: 'pointer',
              fontFamily: 'Space Mono, monospace', fontSize: 10, letterSpacing: 1,
              color: '#6b8fa8',
            }}
          >
            {shown ? 'HIDE' : 'SHOW'}
          </button>
        )}
      </div>
      {showHint && (
        <div id={`${id}-hint`} style={{ marginTop: 5, fontFamily: 'Space Mono, monospace', fontSize: 10, color: '#6b8fa8' }}>
          {hint}
        </div>
      )}
      {error && (
        <div id={`${id}-error`} style={{ marginTop: 5, fontFamily: 'Space Mono, monospace', fontSize: 10, color: '#ff3c6e' }}>
          {error}
        </div>
      )}
    </div>
  );
}

const ALERT_COLORS = { error: '#ff3c6e', success: '#00ff88', warn: '#ffb347' };

export function AuthAlert({ kind = 'error', children }) {
  const c = ALERT_COLORS[kind] || ALERT_COLORS.error;
  return (
    <div
      role={kind === 'error' ? 'alert' : 'status'}
      style={{
        marginBottom: '1rem', padding: '0.7rem 0.9rem', borderRadius: 4,
        border: `1px solid ${c}55`, background: `${c}12`, color: c,
        fontFamily: 'Space Mono, monospace', fontSize: 11, lineHeight: 1.5, textAlign: 'left',
      }}
    >
      {children}
    </div>
  );
}

export default function AuthCard({ title, subtitle, children, footer }) {
  return (
    <div
      className="page-wrapper"
      style={{
        minHeight: '100vh',
        padding: 'calc(64px + 2rem) clamp(1rem, 4vw, 2rem) 4rem',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div className="glass" style={{ width: '100%', maxWidth: 440, padding: 'clamp(1.5rem, 4vw, 2.25rem)' }}>
        <h1 style={{
          fontFamily: 'Orbitron, sans-serif', fontSize: 'clamp(1.25rem, 3.5vw, 1.6rem)',
          fontWeight: 900, color: '#e0f4ff', letterSpacing: '0.05em', marginBottom: '0.4rem',
        }}>
          {title}
        </h1>
        {subtitle && (
          <p style={{ fontFamily: 'Space Mono, monospace', fontSize: 10, letterSpacing: 1, color: '#6b8fa8', marginBottom: '1.75rem' }}>
            {subtitle}
          </p>
        )}
        {children}
        {footer && (
          <div style={{
            marginTop: '1.5rem', paddingTop: '1.1rem', borderTop: '1px solid rgba(0,240,255,0.12)',
            display: 'flex', flexDirection: 'column', gap: '0.75rem', alignItems: 'flex-start',
            fontFamily: 'Space Mono, monospace', fontSize: 11, color: '#6b8fa8',
          }}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
